<template>
  <v-menu v-if="seasonId && clubId && (loading || query.isError.value || hasAlternativeEvent || showWhenSingle)" v-model="menuOpen" location="bottom start">
    <template #activator="{ props: menuProps }">
      <slot name="activator" :props="menuProps" :open="openMenu">
      <v-btn
        v-bind="menuProps"
        class="season-event-switcher"
        variant="tonal"
        :color="buttonColor || 'white'"
        prepend-icon="mdi-calendar-cursor"
        append-icon="mdi-chevron-down"
        :loading="loading"
        :disabled="loading || (!hasAlternativeEvent && !query.isError.value)"
        :aria-label="t('shell.eventSwitcher.change')"
      >
        <span>{{ hideCurrentName ? t('shell.eventSwitcher.change') : selectedEventLabel }}</span>
      </v-btn>
      </slot>
    </template>

    <v-list :disabled="switching" class="season-event-switcher__list" min-width="280">
      <v-list-subheader>{{ t('shell.eventSwitcher.label') }}</v-list-subheader>
      <v-list-item v-if="query.isPending.value" disabled>
        <span role="status">{{ t('app.loading') }}</span>
      </v-list-item>
      <v-list-item v-else-if="query.isError.value">
        <p role="alert" class="season-event-switcher__error">{{ t('shell.eventSwitcher.loadFailed') }}</p>
        <v-btn variant="text" color="primary" :loading="query.isFetching.value" @click.stop="query.refetch()">{{ t('common.actions.refresh') }}</v-btn>
      </v-list-item>
      <template v-else>
      <v-list-item
        v-for="event in visibleEvents"
        :key="event.id"
        :class="{ 'season-event-switcher__event--inactive': event.active === false, 'season-event-switcher__event--active': event.active === true }"
        :title="event.title"
        :subtitle="[event.eventNoun || event.eventType, event.active === false ? t('shell.eventSwitcher.inactive') : ''].filter(Boolean).join(' · ')"
        :prepend-icon="event.active === false ? 'mdi-calendar-remove-outline' : event.id === currentEventId ? 'mdi-check' : 'mdi-calendar-outline'"
        :active="event.id === currentEventId"
        :aria-current="event.id === currentEventId ? 'true' : undefined"
        @click="switchEvent(event.id)"
      >
        <template #append>
          <EventFavoriteButton :club-id="clubId" :season-id="seasonId" :event-id="event.id" :title="event.title" />
        </template>
      </v-list-item>
      <v-list-item
        v-if="visibleEvents.length === 0"
        :title="t('shell.eventSwitcher.empty')"
        disabled
      />
      </template>
    </v-list>
  </v-menu>
  <v-snackbar v-model="switchFailed" color="error">{{ t('shell.eventSwitcher.failed') }}</v-snackbar>
  <span v-if="switching" class="v-sr-only" role="status">{{ t('shell.eventSwitcher.switching') }}</span>
</template>

<script setup lang="ts">
  import EventFavoriteButton from './EventFavoriteButton.vue'
  import { computed, ref, toRef } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { loadRouteLocation, useRoute, useRouter } from 'vue-router'
  import { useQueryClient } from '@tanstack/vue-query'
  import { prepareEventSwitch } from '@/features/navigation/prepareEventSwitch'
  import { canSwitchToEvent, eventSwitchDestination } from '@/features/navigation/eventSwitching'
  import { isDevelopmentProgram, useSeasonEvents } from '@/composables/useSeasonEvents'
  import { useEventFavoritesStore } from '@/stores/eventFavorites'
  import { useBetaStore } from '@/stores/beta'
  import { useAuthStore } from '@/stores/auth'
  import { useEventViewStore, eventViewAccess, resolveEventView } from '@/stores/eventView'
  import { useNavigatorStore } from '@/stores/navigator'

  const props = defineProps<{
    clubId: string
    seasonId: string
    currentEventId?: string
    section?: 'athletes'
    fullscreen?: boolean
    hideCurrentName?: boolean
    buttonColor?: string
    showWhenSingle?: boolean
  }>()


  const menuOpen = ref(false)
  const openMenu = () => { menuOpen.value = true }

  const { t, locale } = useI18n()
  const router = useRouter()
  const route = useRoute()
  const navigatorStore = useNavigatorStore()
  const auth = useAuthStore(), eventViews = useEventViewStore()
  const favorites = useEventFavoritesStore()
  const isFavorite = (eventId: string) => favorites.isFavorite(props.clubId, props.seasonId, eventId)
  const { query, events } = useSeasonEvents(toRef(props, 'clubId'), toRef(props, 'seasonId'))
  const switching = ref(false), switchFailed = ref(false)
  const client = useQueryClient(), beta = useBetaStore()
  const loading = computed(() => query.isPending.value || switching.value)

  const currentEvent = computed(() => events.value.find(event => event.id === props.currentEventId) || null)
  const staffEvents = computed(() => events.value.filter(event => {
    // Use the shared season catalogue so this also applies to breadcrumb and fullscreen menus.
    if (props.currentEventId && (!currentEvent.value ||
      isDevelopmentProgram(event.eventType) !== isDevelopmentProgram(currentEvent.value.eventType))) return false
    return canSwitchToEvent(route, event, props.section)
  }))
  const visibleEvents = computed(() => [...staffEvents.value]
    .sort((a, b) => Number(isFavorite(b.id)) - Number(isFavorite(a.id)) || a.title.localeCompare(b.title, locale.value, { sensitivity: 'base' })))

  const hasAlternativeEvent = computed(() =>
    visibleEvents.value.some((event) => event.id !== props.currentEventId)
  )

  const selectedEvent = computed(() =>
    visibleEvents.value.find((event) => event.id === props.currentEventId) || currentEvent.value
  )

  const selectedEventLabel = computed(() =>
    selectedEvent.value?.title || t('shell.eventSwitcher.placeholder')
  )

  const switchEvent = async (eventId: string) => {
    if (switching.value || !eventId || eventId === props.currentEventId) return
    const event = visibleEvents.value.find(event => event.id === eventId)
    if (!event) return
    const destination = props.currentEventId
      ? eventSwitchDestination(route, event, { section: props.section, fullscreen: props.fullscreen })
      : `/clubhouse/${props.clubId}/season/${props.seasonId}/tryout/${eventId}`
    const viewerId = String(auth.user?.details?.id || auth.user?.id || '')
    const scope = { viewerId, clubId: props.clubId, seasonId: props.seasonId, eventId }
    const currentMode = resolveEventView(eventViews.preference({ ...scope, eventId: props.currentEventId || '' }), currentEvent.value?.roles || {})
    const preferredMode = route.meta.eventView === 'athlete' ? 'athlete' : route.meta.eventView === 'shared' ? currentMode : 'staff'
    const access = eventViewAccess(event.roles)
    const nextMode = preferredMode === 'athlete' && access.athlete || !access.staff ? 'athlete' : 'staff'
    const from = route.fullPath
    const target = router.resolve(destination)
    switching.value = true
    switchFailed.value = false
    try {
      await Promise.all([
        prepareEventSwitch(client, target, { id: String(auth.user?.id || ''), homeId: viewerId, beta: beta.enabled }),
        loadRouteLocation(target),
      ])
      // A separate navigation or account change cancels this pending handover.
      if (route.fullPath !== from || String(auth.user?.details?.id || auth.user?.id || '') !== viewerId) return
      const failure = await router.push(destination)
      if (!failure) {
        eventViews.select(scope, nextMode, event.roles || {})
        navigatorStore.selectTryout(eventId)
      }
    } catch {
      if (route.fullPath === from) switchFailed.value = true
    } finally {
      switching.value = false
    }
  }

</script>

<style scoped>
  .season-event-switcher {
    max-width: min(360px, 38vw);
    min-width: 0;
    border-radius: 8px;
    font-weight: 800;
    text-transform: none;
  }

  .season-event-switcher :deep(.v-btn__content) {
    min-width: 0;
  }

  .season-event-switcher span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .season-event-switcher__list {
    max-width: 420px;
  }

  .season-event-switcher__error {
    color: rgb(var(--v-theme-error));
    white-space: normal;
  }

  .season-event-switcher__event--active :deep(.v-list-item__prepend > .v-icon) {
    color: rgb(var(--v-theme-success));
    opacity: 1;
  }

  .season-event-switcher__event--inactive :deep(.v-list-item__prepend > .v-icon) {
    color: var(--cs-muted);
    opacity: 0.45;
  }

  @media (max-width: 760px) {
    .season-event-switcher {
      max-width: 100%;
      width: 100%;
    }
  }
</style>
